import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { LucideModule } from '@shared/modules/lucide.module';

export type FileType = 'img' | 'pdf' | 'doc' | 'ppt' | 'xls' | 'video' | 'audio' | 'zip' | 'unknown';

@Component({
	selector: 'app-file-icon',
	imports: [CommonModule, LucideModule],
	templateUrl: './file-icon.component.html',
	styleUrl: './file-icon.component.scss',
})
export class FileIconComponent {
	@Input() fileType: FileType = 'unknown';
	@Input() fileName?: string;

	/**
	 * Detects the file type based on the file name's extension
	 */
	ngOnInit() {
		if (this.fileName && this.fileType === 'unknown') {
			this.fileType = this.detectFileType(this.fileName);
		}
	}

	/**
	 * Detects the file type based on the extension
	 */
	private detectFileType(fileName: string): FileType {
		const extension = fileName.split('.').pop()?.toLowerCase();

		switch (extension) {
			case 'jpg':
			case 'jpeg':
			case 'png':
			case 'gif':
			case 'webp':
			case 'svg':
			case 'bmp':
				return 'img';

			case 'pdf':
				return 'pdf';

			case 'mp4':
			case 'mov':
			case 'avi':
			case 'webm':
			case 'mkv':
			case 'm4v':
				return 'video';

			case 'mp3':
			case 'wav':
			case 'm4a':
			case 'ogg':
				return 'audio';

			case 'zip':
			case 'rar':
			case '7z':
				return 'zip';

			case 'doc':
			case 'docx':
				return 'doc';

			case 'ppt':
			case 'pptx':
				return 'ppt';

			case 'xls':
			case 'xlsx':
			case 'csv':
				return 'xls';

			default:
				return 'unknown';
		}
	}

	/**
	 * Returns the extension to display in the badge
	 */
	getFileExtension(): string {
		if (this.fileName) {
			const ext = this.fileName.split('.').pop()?.toUpperCase();
			return ext || 'FILE';
		}

		switch (this.fileType) {
			case 'img':
				return 'IMG';
			case 'pdf':
				return 'PDF';
			case 'doc':
				return 'DOC';
			case 'ppt':
				return 'PPT';
			case 'xls':
				return 'XLS';
			case 'video':
				return 'VIDEO';
			case 'audio':
				return 'AUDIO';
			case 'zip':
				return 'ZIP';
			default:
				return 'FILE';
		}
	}

	/** Lucide icon that represents the file type. */
	getIconName(): string {
		switch (this.fileType) {
			case 'img':
				return 'Image';
			case 'pdf':
				return 'FileText';
			case 'doc':
				return 'FileText';
			case 'ppt':
				return 'FileSpreadsheet';
			case 'xls':
				return 'FileSpreadsheet';
			case 'video':
				return 'PlayCircle';
			case 'audio':
				return 'Mic';
			case 'zip':
				return 'FileArchive';
			default:
				return 'File';
		}
	}
}
